import React, { FC, useEffect, useState } from 'react';

import {
	ArrayElement, Checkbox, Counter, IColumnEditor, Label, Menu,
} from '@aleph/shared-components';
import {
	getRedditInterestOptions,
	RedditCategorisedInterestOptions,
} from 'wb-reddit/app/routes/superGrid/targeting/utils';
import { useApolloClient } from '@apollo/client';
import { Loader } from 'rsuite';
import { InterestOptionsQuery } from 'wb-reddit/graphql/generated';

const InterestsColumnEditor: FC<IColumnEditor<InterestOptionsQuery['options']['items']>> = (params) => {
	const { value, setValue, editorContext } = params;
	const client = useApolloClient();

	const [loading, setLoading] = useState<boolean>(true);
	const [options, setOptions] = useState<RedditCategorisedInterestOptions>({});

	const getDevices = async () => {
		setLoading(true);

		const redditOptions = await getRedditInterestOptions({
			client,
			search: editorContext?.search,
		});

		setLoading(false);
		setOptions(redditOptions);
	};

	const toggleValue = (option: ArrayElement<RedditCategorisedInterestOptions[string]>) => () => {
		if (value?.some(v => v?.value === option?.value)) {
			setValue(value?.filter(v => v?.value !== option?.value));

			return;
		}

		setValue([
			...(value ?? []),
			option,
		]);
	};

	useEffect(() => {
		if (editorContext) {
			getDevices();
		}
	}, [editorContext?.search ?? '']);

	return (
		<div
			className="flex flex-col h-full max-w-full p-[8px]"
		>
			{
				loading
				&& <Loader
					vertical
					size="md"
					className="flex-1 !flex items-center justify-center"
					content="Loading interests"
				/>
			}

			{
				!loading
				&& <div
					className="flex whitespace-nowrap items-center gap-[8px] text-[13px] py-[4px] px-[4px]"
				>
					<div className="flex gap-[8px] flex-1 justify-between items-center">
						<div className="text-gray-600 text-[12px]">
							INTERESTS
						</div>

						{
							(value?.length ?? 0) > 0
							&& <Counter
								className="relative"
								variant="square-accent"
								count={value?.length ?? 0}
							/>
						}
					</div>
				</div>
			}

			{!loading
				&& <Menu className="max-w-full">
					<>
						{Object.keys(options).map(interestCategory => {
							const categoryItemCount = options?.[interestCategory]?.length || 0;
							const selectedCategoryItemCount = value
								?.filter(v => v?.category === interestCategory).length || 0;
							const categoryActive = value?.some(v => v?.category === interestCategory);
							const partiallySelected = selectedCategoryItemCount > 0
								&& selectedCategoryItemCount < categoryItemCount;

							const subMenuItems = (options[interestCategory] || []).map(option => (
								<Menu.Item 	onClick={toggleValue(option)}
									selected={value?.some(v => v?.value === option?.value)}
									alignItems="space-between"
									key={option.value}
								>
									<Label>
										{option.label}
									</Label>
									<Checkbox checked={value?.some(v => v?.value === option?.value)}/>
								</Menu.Item>
							));

							return (
								<Menu.Category subMenuItems={subMenuItems}
									key={interestCategory}
								>
									<Label>{interestCategory}</Label>
									<Checkbox
										onClick={() => (categoryActive ? setValue(
											(value ?? []).filter(
												val => val.category !== interestCategory
													&& !options?.[interestCategory]?.some(v => v?.value === val?.value),
											),
										) : value
											? setValue([
												...value,
												...(options?.[interestCategory] ?? []),
											]) : undefined)
										}
										checked={(value?.some(v => v?.category === interestCategory)
											|| options?.[interestCategory]
												?.some(v => value
													?.some(sv => sv?.value === v?.value)))
											&& !partiallySelected}
										partial={
											partiallySelected
										}
									/>
								</Menu.Category>
							);
						})}
					</>
				</Menu>
			}
		</div>
	);
};

export default InterestsColumnEditor;
